// tour.interfaces.ts
import { PopperPlacement } from 'shepherd.js';

export interface TourStep {
  id: string;
  text: string;
  element: string;
  position: PopperPlacement;
  buttonText: string;
}

export interface TourOptions {
  persistent?: boolean;
  storageKey?: string;
  onComplete?: () => void;
}

// tour.service.ts
import { Injectable } from '@angular/core';
import { offset } from '@floating-ui/dom';
import Shepherd from 'shepherd.js';
import type { Tour } from 'shepherd.js';

@Injectable()
export class TourService {
  private tour?: Tour;
  private options?: TourOptions;

  constructor() {}

  initialize(steps: TourStep[], options?: TourOptions): TourService {
    const defaultConfig = {
      useModalOverlay: true,
      defaultStepOptions: {
        modalOverlayOpeningPadding: 10,
        modalOverlayOpeningRadius: 8,
      },
    };

    this.tour = new Shepherd.Tour({
      ...defaultConfig,
    });

    this.options = options;
    this.initTour(steps, options);

    return this;
  }

  private initTour(steps: TourStep[], options?: TourOptions): void {
    steps.forEach(step => {
      this.tour?.addStep({
        id: step.id,
        text: step.text,
        attachTo: { element: step.element, on: step.position },
        floatingUIOptions: { middleware: [offset(25)] },
        buttons: [{ text: step.buttonText, action: this.tour.next }],
      });
    });

    if (options?.persistent) {
      this.tour?.on('complete', () => {
        this.markTourAsCompleted(options.storageKey!);
        options.onComplete?.();
      });
    } else if (options?.onComplete) {
      this.tour?.on('complete', options.onComplete);
    }
  }

  start(skipPersistenceCheck: boolean = false): void {
    if (
      !skipPersistenceCheck &&
      this.options?.persistent &&
      this.isTourCompleted(this.options.storageKey)
    ) {
      return;
    }

    if (this.tour && this.tour?.steps.length > 0) {
      this.tour?.start();
    }
  }

  complete(): void {
    this.tour?.complete();
  }

  isActive(): boolean {
    return this.tour != undefined && this.tour!.isActive();
  }

  private isTourCompleted(storageKey?: string): boolean {
    if (!storageKey) return false;
    return localStorage.getItem(storageKey) === 'true';
  }

  private markTourAsCompleted(storageKey: string): void {
    localStorage.setItem(storageKey, 'true');
  }

  public handleOutsideClick = (event: MouseEvent) => {
    console.log('handleOutsideClick', this.tour);
    if (!this.tour || !this.tour.isActive()) return;

    const target = event.target as Element;
    const isTourElement = target.closest('.shepherd-element, .shepherd-modal-overlay');
    if (!isTourElement) {
      this.tour.complete(); // Fecha o tour

      // Remove o event listener após o fechamento
      document.removeEventListener('click', this.handleOutsideClick);
    }
  };
}

// tour.builder.ts

@Injectable({
  providedIn: 'root',
})
export class TourBuilder {
  build(steps: TourStep[], options?: TourOptions): TourService {
    return new TourService().initialize(steps, options);
  }
}
